import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import classNames from 'classnames';

import UserService from '../../services/UserService';
import SupportService from '../../services/SupportService';
import FilledButton from '../Buttons/FilledButton';
import OutlinedButton from '../Buttons/OutlinedButton';
import Textarea from '../Textarea';
import { USER_ROLES } from '../../constants/main';
import { UiContext } from '../../context/UiContext';
import { useUiStore } from '../../store';

import classes from './styles.module.scss';

export default function ContactUsWidget() {
  const { notifySuccess, notifyError } = useContext(UiContext);
  const { data: currentUser } = useQuery('me', UserService.getMe);
  const isOpen = useUiStore((state) => state.isContactUsOpen);
  const setContactUsOpen = useUiStore((state) => state.setContactUsOpen);
  const setOnboardingVideoOpen = useUiStore((state) => state.setOnboardingVideoOpen);

  const isSuperAdmin =
    currentUser?.role === USER_ROLES.SUPER_ADMIN &&
    !currentUser?.impersonation?.isImpersonating;

  const [message, setMessage] = useState('');
  const [view, setView] = useState('choice');

  const trimmedMessage = useMemo(() => message.trim(), [message]);

  useEffect(() => {
    if (isOpen) {
      setView('choice');
    }
  }, [isOpen]);

  const createTicketMutation = useMutation(SupportService.createTicket, {
    onSuccess: () => {
      notifySuccess('Message sent to support');
      setMessage('');
      setContactUsOpen(false);
    },
    onError: (error) => {
      notifyError(
        error?.response?.data?.message || 'Failed to send message to support'
      );
    },
  });

  const handleSend = useCallback(() => {
    if (!trimmedMessage || createTicketMutation.isLoading) return;
    createTicketMutation.mutate({ message: trimmedMessage });
  }, [trimmedMessage, createTicketMutation]);

  if (!currentUser || isSuperAdmin) {
    return null;
  }

  const handleClose = () => setContactUsOpen(false);

  const handleOpenVideoTutorials = () => {
    setContactUsOpen(false);
    setOnboardingVideoOpen(true);
  };

  const isChoiceView = view === 'choice';

  return (
    <>
      <div
        className={classNames(classes.panel, {
          [classes.open]: isOpen,
        })}
        role={isOpen ? 'dialog' : undefined}
        aria-label={isOpen ? 'Contact us form' : undefined}
      >
        <div className={classes.header}>
          <div className={classes.title}>Get Help</div>
          <button
            type="button"
            className={classes.closeButton}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {isChoiceView ? (
          <div className={classes.choiceActions}>
            <OutlinedButton
              className={classes.choiceButton}
              onClick={handleOpenVideoTutorials}
            >
              Video Tutorials
            </OutlinedButton>
            <FilledButton
              className={classes.choiceButton}
              onClick={() => setView('form')}
            >
              Contact us
            </FilledButton>
          </div>
        ) : (
          <>
            <div className={classes.userInfo}>
              <div className={classes.userRow}>
                <span className={classes.userLabel}>Sender Name</span>
                <span className={classes.userValue}>{currentUser?.name || '-'}</span>
              </div>
              <div className={classes.userRow}>
                <span className={classes.userLabel}>Sender Email</span>
                <span className={classes.userValue}>{currentUser?.email || '-'}</span>
              </div>
            </div>

            <div className={classes.form}>
              <Textarea
                placeholder="Send us a message or report an issue"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={createTicketMutation.isLoading}
              />

              <div className={classes.actions}>
                <FilledButton
                  onClick={handleSend}
                  isDisabled={!trimmedMessage || createTicketMutation.isLoading}
                >
                  {createTicketMutation.isLoading ? 'Sending…' : 'Send'}
                </FilledButton>
              </div>
            </div>
          </>
        )}
      </div>

      {isOpen && (
        <button
          type="button"
          className={classes.backdrop}
          onClick={handleClose}
          aria-label="Close contact us"
        />
      )}
    </>
  );
}
